import { makeAutoObservable, runInAction } from "mobx";
import type { IntegrationCapabilityService } from "@/services/integration-capability.service";
import type { TConnectorCapability } from "@/types/integration-capabilities";

/** One workspace's answer: what its connectors supply, or that the read failed. A
 *  workspace with no entry has not been answered yet. */
type TCapabilityAnswer =
  | { status: "ready"; capabilities: readonly TConnectorCapability[] }
  | { status: "failed" };

/** What the store knows about one capability on one workspace.
 *
 *  `failed` is its own answer rather than folded into `absent`, because the two call for
 *  opposite UI: a workspace we know has no connector hides the surface, and one we could
 *  not ask about keeps it (see {@link useDeliveryResultsAvailability}). */
export type TCapabilityVerdict = "supplied" | "absent" | "failed" | "pending";

/** What each workspace's connectors can supply (NG-2265), by slug.
 *
 *  Read by the surfaces that open only while a connector supplies delivery/results data:
 *  a project's Delivery and Results tabs and the Existing content panel on Plan work.
 *  Those are project and request surfaces, whose readers cannot list the workspace's
 *  connections (`workspace.edit`), so they ask this instead.
 *
 *  **Refreshed, not cached for the session.** The ticket's rule is that removing the last
 *  connector hides the surfaces and adding one shows them, immediately. So the surfaces
 *  refresh on entry (each project, each request), and the connection store refreshes after
 *  its own connect and disconnect, which is the same tab acting on its own change. A
 *  refresh keeps the previous answer on screen while it is in flight, so moving between
 *  projects never flickers a tab out and back.
 */
export class WorkspaceCapabilityStore {
  answers: Record<string, TCapabilityAnswer> = {};

  /** The service rather than the root store, so this module does not import
   *  `root.store` and close a cycle the dependency ratchet refuses (the
   *  `capacity-rebalance.store.ts` shape). Held whole, not as a bound method, so a test
   *  that mocks the service's methods after construction is the one this calls. */
  private service: IntegrationCapabilityService;
  /** One request per workspace at a time: the tab row and the page under it both ask on
   *  mount, and the second should ride the first. */
  private inFlight = new Map<string, Promise<void>>();

  constructor(service: IntegrationCapabilityService) {
    this.service = service;
    makeAutoObservable(this, { service: false, inFlight: false } as never, { autoBind: true });
  }

  verdict(slug: string, capability: TConnectorCapability): TCapabilityVerdict {
    const answer = this.answers[slug];
    if (!answer) return "pending";
    if (answer.status === "failed") return "failed";
    return answer.capabilities.includes(capability) ? "supplied" : "absent";
  }

  /** Ask the server again. Never rejects: a failure is logged and recorded as an answer. */
  refresh(slug: string): Promise<void> {
    const pending = this.inFlight.get(slug);
    if (pending) return pending;

    const request = (async () => {
      try {
        const body = await this.service.getCapabilities(slug);
        runInAction(() => {
          this.answers[slug] = { status: "ready", capabilities: body.capabilities };
        });
      } catch (error) {
        // A warn, not an error: the surfaces fall back to showing, which is what they did
        // before this gate existed, so nothing the user asked for is broken.
        console.warn("[integrations] capability read failed; keeping the last answer", {
          slug,
          error,
        });
        runInAction(() => {
          // A stale answer beats no answer: keep a ready one, record the failure only
          // when there is nothing else to go on.
          if (this.answers[slug]?.status !== "ready") this.answers[slug] = { status: "failed" };
        });
      } finally {
        this.inFlight.delete(slug);
      }
    })();
    this.inFlight.set(slug, request);
    return request;
  }

  /** Ask only if this workspace has never been answered: for a surface that sits under
   *  one which already refreshes on entry. */
  ensure(slug: string): Promise<void> {
    return this.answers[slug] ? Promise.resolve() : this.refresh(slug);
  }
}
